import {Injectable} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {AuthService} from '@shared/services/auth.service';
import {hasEnterpriseFeatures, PermissionOptions} from '@users/models/user.interface';
import type {EmailComposeComponent} from 'app/modules/admin/outbox/features/emails/components/compose-email/compose-email.component';
import {OutboxService} from 'app/modules/admin/outbox/services/outbox.service';
import {UpgradeDialogComponent} from 'app/modules/admin/tasks/components/upgrade-dialog/upgrade-dialog.component';
import {take} from 'rxjs/operators';
import {buildSteuerberaterContact} from '../export/vat-mail-draft';

export type VatMailGate = 'OK' | 'UPGRADE' | 'NO_PERMISSION';

export interface VatMailOpenParams {
    landlordID: string;
    steuerberater: { name: string; email: string };
    subject: string;
    body: { html: string; text: string };
    files: File[];
}

/**
 * Steuerberater-Mail (Spec 7, A7): Gate wie der bestehende Postausgang (Enterprise → UpgradeDialog, dann
 * OUTBOX_WRITE), Composer über OutboxService (disableClose, maxHeight 90vh). NUR Entwurf – kein Versand.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable({providedIn: 'root'})
export class VatMailService {

    constructor(
        private authService: AuthService,
        private outboxService: OutboxService,
        private dialog: MatDialog,
        private router: Router,
    ) {
    }

    resolveGate(): VatMailGate {
        // hasEnterpriseFeatures statt `=== ENTERPRISE`: CUSTOM liegt über ENTERPRISE und darf nicht ausgesperrt werden.
        if (!hasEnterpriseFeatures(this.authService.tier)) {
            return 'UPGRADE';
        }
        if (!this.authService.hasPermission(PermissionOptions.OUTBOX_WRITE)) {
            return 'NO_PERMISSION';
        }
        return 'OK';
    }

    /** Identisch zu landlords/components/details/details.component.ts `upgradeDialog()`. */
    openUpgradeDialog(): void {
        const dialogRef = this.dialog.open(UpgradeDialogComponent, {width: '80%', height: '90%'});
        dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
            if (result === 'CONTINUE') {
                void this.router.navigate(['/upgrade']);
            }
        });
    }

    /** Öffnet den Composer als Entwurf; `null`, wenn der OutboxService stattdessen (fehlende Berechtigung) navigiert hat. */
    openTaxAdvisorDraft(params: VatMailOpenParams): MatDialogRef<EmailComposeComponent> | null {
        const contact = buildSteuerberaterContact(params.steuerberater, params.landlordID);
        const ref = this.outboxService.openEmailComposeDialog({
            contact,
            contacts: [contact],
            files: params.files,
            messageContent: {subject: params.subject, html: params.body.html, text: params.body.text},
            stayOnPage: true,
            isOutbox: false,
        });
        return ref instanceof MatDialogRef ? ref : null;
    }
}
